/**
 * Assign M3 SKU segments from PDM `base_part_number` onto the body/rows option
 * that actually distinguishes those bases (often middle row, not always body-step).
 *
 * Example (Chevrolet Traverse):
 * - bases `CHE2408E` / `CHE2408F` differ by Middle Row
 * - vehicle.sku `CHE` → option SKUs `2408E` / `2408F`
 * - custom M3 then becomes `CHE2408E-…` / `CHE2408F-…` when middle changes
 *
 * Factory match already tracks this via full `part_number`; custom M3 needs the
 * short segment on the selected option.
 */

import type { OptionValue, Step, Substep } from "@views/Upholstery/types"
import type { PdmFlatRow } from "../../pdm/normalizePdm"
import { getSeatAndRowSlices, unwrapValue } from "../../pdm/normalizePdm"
import { findDatoOptionMatch, isM3BodyRowsSkuSegment } from "./pdm-option-sku"

type Discriminator = {
  stepKey: "body" | "rows"
  substepKey: string
  /** PDM attribute / application value for this product */
  value: string
}

function productBase(product: Record<string, unknown>): string {
  return String(product.base_part_number ?? product.basePartNumber ?? "")
    .replace(/\s+/g, "")
    .trim()
}

function discriminatorsForRow(row: PdmFlatRow): Discriminator[] {
  const { product, application } = row
  const seats = getSeatAndRowSlices(product)
  const trim = unwrapValue(application.sub_model ?? application.SubModel ?? "")
  const bodyType = unwrapValue(
    application.body_type ?? application.bodyType ?? application.BodyType ?? ""
  )

  const out: Discriminator[] = []
  if (trim) out.push({ stepKey: "body", substepKey: "trim", value: trim })
  if (bodyType)
    out.push({ stepKey: "body", substepKey: "body-step", value: bodyType })
  if (seats.driverSeat)
    out.push({
      stepKey: "body",
      substepKey: "driver-seat-type",
      value: seats.driverSeat,
    })
  if (seats.passengerSeat)
    out.push({
      stepKey: "body",
      substepKey: "passenger-seat-type",
      value: seats.passengerSeat,
    })
  if (seats.frontRow)
    out.push({ stepKey: "rows", substepKey: "front", value: seats.frontRow })
  if (seats.middleRow)
    out.push({ stepKey: "rows", substepKey: "middle", value: seats.middleRow })
  if (seats.backRow)
    out.push({ stepKey: "rows", substepKey: "back", value: seats.backRow })
  return out
}

/** Prefer row middle, then body type, then other dimensions. */
const DISCRIMINATOR_PRIORITY: Array<{ stepKey: string; substepKey: string }> = [
  { stepKey: "rows", substepKey: "middle" },
  { stepKey: "body", substepKey: "body-step" },
  { stepKey: "rows", substepKey: "back" },
  { stepKey: "rows", substepKey: "front" },
  { stepKey: "body", substepKey: "trim" },
  { stepKey: "body", substepKey: "driver-seat-type" },
  { stepKey: "body", substepKey: "passenger-seat-type" },
]

function pickVaryingDiscriminator(
  byBase: Map<string, Discriminator[]>
): { stepKey: "body" | "rows"; substepKey: string } | null {
  const bases = Array.from(byBase.keys())
  if (bases.length < 2) return null

  for (const candidate of DISCRIMINATOR_PRIORITY) {
    const valueToBases = new Map<string, Set<string>>()
    let complete = true
    for (const base of bases) {
      const values = Array.from(
        new Set(
          (byBase.get(base) ?? [])
            .filter(
              (d) =>
                d.stepKey === candidate.stepKey &&
                d.substepKey === candidate.substepKey
            )
            .map((d) => d.value.trim())
            .filter(Boolean)
        )
      )
      if (!values.length) {
        complete = false
        break
      }
      for (const value of values) {
        const owners = valueToBases.get(value) ?? new Set<string>()
        owners.add(base)
        valueToBases.set(value, owners)
      }
    }
    if (!complete) continue

    // Each option value must belong to exactly one base. One base may
    // still cover several values (Civic hatchback kit on both LX and Sport).
    const eachValueHasOneBase = Array.from(valueToBases.values()).every(
      (owners) => owners.size === 1
    )
    if (eachValueHasOneBase && valueToBases.size >= 2) {
      return {
        stepKey: candidate.stepKey as "body" | "rows",
        substepKey: candidate.substepKey,
      }
    }
  }

  return null
}

function skuSuffixForBase(base: string, vehicleSku: string): string {
  const v = vehicleSku.replace(/\s+/g, "").trim()
  if (v && base.startsWith(v) && base.length > v.length) {
    return base.slice(v.length)
  }
  return base
}

function assignSkuToMatchingOptions(
  options: OptionValue[],
  valueToSku: Map<string, string>
): OptionValue[] {
  const next = options.map((opt) => ({ ...opt }))

  for (const [pdmValue, sku] of Array.from(valueToSku.entries())) {
    const match = findDatoOptionMatch(next, pdmValue)
    if (!match) continue
    const index = next.findIndex((o) => o === match || o.id === match.id)
    const target = index >= 0 ? next[index] : match
    if (!target || isM3BodyRowsSkuSegment(target.sku ?? "")) continue
    if (index >= 0) {
      next[index] = { ...next[index], sku }
    }
  }

  return next
}

/**
 * Fill empty body/rows option SKUs from PDM base_part_number remainders when
 * multiple bases share a vehicle prefix and differ on one config dimension.
 */
export function applyDerivedM3SuffixesFromPdmBases(
  steps: Step[],
  flat: PdmFlatRow[],
  vehicleSku: string
): Step[] {
  if (!flat.length) return steps

  const byBase = new Map<string, Discriminator[]>()
  for (const row of flat) {
    const base = productBase(row.product)
    if (!base) continue
    const existing = byBase.get(base) ?? []
    existing.push(...discriminatorsForRow(row))
    byBase.set(base, existing)
  }

  if (byBase.size < 2) return steps

  const varying = pickVaryingDiscriminator(byBase)
  if (!varying) return steps

  const valueToSku = new Map<string, string>()
  for (const [base, discs] of Array.from(byBase.entries())) {
    const suffix = skuSuffixForBase(base, vehicleSku)
    if (!suffix || !isM3BodyRowsSkuSegment(suffix)) continue
    const values = discs
      .filter(
        (d) =>
          d.stepKey === varying.stepKey && d.substepKey === varying.substepKey
      )
      .map((d) => d.value.trim())
      .filter(Boolean)
    for (const value of values) {
      if (!valueToSku.has(value)) {
        valueToSku.set(value, suffix)
      }
    }
  }

  if (!valueToSku.size) return steps

  return steps.map((step) => {
    if (step.key !== varying.stepKey) return step

    const substeps: Substep[] = (step.substeps ?? []).map((substep) => {
      if (substep.key !== varying.substepKey) return substep
      const opts = substep.category?.optionValues ?? []
      if (!opts.length) return substep

      return {
        ...substep,
        category: {
          ...substep.category,
          optionValues: assignSkuToMatchingOptions(opts, valueToSku),
        },
      }
    })

    return { ...step, substeps }
  })
}
